import {useFeatureAppEnv, useFeatureFlags} from '@smart/feature-app';
import type {
  FinanceParamsV1,
  LeasingParamsV1,
  PurchaseItemV1,
} from '@smart/payment-config-service';
import type {PriceTypeV1} from '@smart/price-preferences-provider';
import * as React from 'react';
import type {PriceDetailsByType} from '../../bff/price-details';
import {FEATURE_FLAG} from '../constants';
import {fetchPriceDetails} from '../utils/fetch-price-details';
import type {DelayedStateResult} from './use-delayed-state';
import {useDelayedState} from './use-delayed-state';
import {useLocale} from './use-locale';

export interface UsePriceDetailsProps {
  readonly params: LeasingParamsV1 | FinanceParamsV1;
  readonly purchaseItem: PurchaseItemV1;
  readonly priceType: PriceTypeV1;
}

export type MaybePriceDetails =
  | {readonly priceDetails: PriceDetailsByType}
  | {readonly error: unknown};

export function usePriceDetails({
  params,
  purchaseItem,
  priceType,
}: UsePriceDetailsProps): [
  DelayedStateResult<MaybePriceDetails>,
  () => void,
  () => void,
] {
  const [priceDetailsResult, setPriceDetails] =
    useDelayedState<MaybePriceDetails>();

  const {baseUrl = ``, featureServices} = useFeatureAppEnv();
  const locale = useLocale();
  const {
    'endpoint-directory': endpointDirectory,
    's2:logger': logger = console,
  } = featureServices;

  const abortControllerRef = React.useRef<AbortController>();

  const {enabled: enableAnonymousFlow} = useFeatureFlags(
    FEATURE_FLAG.ENABLE_ANONYMOUS_FLOW,
  );

  const fetchPriceDetailsCallback = React.useCallback(() => {
    setPriceDetails(undefined);
    abortControllerRef.current?.abort();

    const abortController = (abortControllerRef.current =
      new AbortController());

    fetchPriceDetails(baseUrl, {
      endpointDirectory,
      locale,
      purchaseItem,
      params,
      priceType,
      signal: abortController.signal,
      enableAnonymousFlow,
    })
      .then((priceDetails) => setPriceDetails({priceDetails}))
      .catch((error: unknown) => {
        if (!abortController.signal.aborted) {
          logger.error(error);
          setPriceDetails({error});
        }
      });
  }, [
    baseUrl,
    endpointDirectory,
    logger,
    locale,
    purchaseItem,
    JSON.stringify(params),
  ]);

  //React.useEffect(fetchPriceDetailsCallback, [fetchPriceDetailsCallback]);
  React.useEffect(() => () => abortControllerRef.current?.abort(), []);
  return [
    priceDetailsResult,
    fetchPriceDetailsCallback,
    () => setPriceDetails(undefined),
  ];
}
